import { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache shared across hook instances to avoid duplicate fetches
let _cache = { data: null, ts: 0 };
let _inflight = null;
const CACHE_MS = 4500; // refresh every ~5s

const fetchAssets = async () => {
  const res = await fetch(`${API}/api/assets?active_only=true`);
  if (!res.ok) throw new Error(`Assets fetch failed: ${res.status}`);
  return res.json();
};

const sharedFetch = async () => {
  const now = Date.now();
  if (_cache.data && now - _cache.ts < CACHE_MS) return _cache.data;
  if (_inflight) return _inflight;
  _inflight = fetchAssets()
    .then((data) => {
      _cache = { data, ts: Date.now() };
      _inflight = null;
      return data;
    })
    .catch((e) => {
      _inflight = null;
      throw e;
    });
  return _inflight;
};

/**
 * Returns a map keyed by symbol of live prices: { 'EUR/USD': { bid, ask, change_24h, ... }, ... }
 * Auto-refreshes every 5s.
 *
 * @param {string[]} symbols — optional filter; pass empty/undefined to get all assets
 * @param {number} intervalMs — poll interval (default 5000)
 */
export default function useLivePrices(symbols, intervalMs = 5000) {
  const [bySymbol, setBySymbol] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tick = useCallback(async () => {
    try {
      const data = await sharedFetch();
      const map = {};
      for (const a of data) map[a.symbol] = a;
      setBySymbol(map);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [tick, intervalMs]);

  // optional filter
  if (symbols && symbols.length) {
    const filtered = {};
    for (const s of symbols) {
      if (bySymbol[s]) filtered[s] = bySymbol[s];
    }
    return { prices: filtered, loading, error };
  }
  return { prices: bySymbol, loading, error };
}

/**
 * Format a numerical price based on category.
 */
export function formatPrice(price, category, symbol) {
  if (price == null || isNaN(price)) return '—';
  if (category === 'forex') {
    if (symbol && symbol.includes('JPY')) return Number(price).toFixed(3);
    return Number(price).toFixed(5);
  }
  if (category === 'commodities' && (symbol === 'XAU/USD' || symbol === 'XAG/USD')) {
    return Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (category === 'crypto') {
    if (price > 100) return Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 });
    return Number(price).toFixed(2);
  }
  if (category === 'indices') return Number(price).toLocaleString('en-US', { maximumFractionDigits: 1 });
  return Number(price).toFixed(2);
}

export function formatChange(pct) {
  if (pct == null || isNaN(pct)) return '0.00%';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${Number(pct).toFixed(2)}%`;
}
